import {
  createContext,
  JSX,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useRef,
} from 'react';

type BackOverride = (() => void) | null;

interface InterviewOverlayContextValue {
  getBackOverride: () => BackOverride;
  setBackOverride: (cb: BackOverride) => void;
  contentRef: RefObject<HTMLDivElement | null>;
}

const InterviewOverlayContext =
  createContext<InterviewOverlayContextValue | null>(null);

export function InterviewOverlayProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const backOverrideRef = useRef<BackOverride>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const getBackOverride = useCallback(() => backOverrideRef.current, []);
  const setBackOverride = useCallback((cb: BackOverride) => {
    backOverrideRef.current = cb;
  }, []);

  return (
    <InterviewOverlayContext.Provider
      value={{ getBackOverride, setBackOverride, contentRef }}
    >
      {children}
    </InterviewOverlayContext.Provider>
  );
}

export function useInterviewOverlay(): InterviewOverlayContextValue {
  const ctx = useContext(InterviewOverlayContext);
  if (!ctx) {
    throw new Error(
      'useInterviewOverlay must be used within InterviewOverlayProvider'
    );
  }
  return ctx;
}
